import { createContext, useContext, ReactNode } from 'react';

export interface CleverTapStatus {
  /** The web SDK has finished `init()` (`window.clevertap` is available). */
  ready: boolean;
  /**
   * Analytics consent (CookieYes) has been granted.
   *
   * Since FFH-2334 consent gates `init()` itself, so `ready` implies this — a
   * write only needs `ready`. It stays exposed because it resolves earlier than
   * `ready` (consumers can tell "consent granted, SDK still starting" apart
   * from "consent denied, SDK will never start"), and because it flips back on
   * a mid-session withdrawal while `ready` remains true until the reload.
   */
  consentGranted: boolean;
}

const DEFAULT_STATUS: CleverTapStatus = {
  ready: false,
  consentGranted: false
};

const CleverTapStatusContext = createContext<CleverTapStatus>(DEFAULT_STATUS);

export const CleverTapStatusProvider = ({
  value,
  children
}: {
  value: CleverTapStatus;
  children: ReactNode;
}) => (
  <CleverTapStatusContext.Provider value={value}>
    {children}
  </CleverTapStatusContext.Provider>
);

/**
 * Read the CleverTap SDK's readiness + consent state anywhere in the tree.
 */
export const useCleverTapStatus = (): CleverTapStatus =>
  useContext(CleverTapStatusContext);
